import { FC, useEffect, useMemo, useState } from "react";
import HorizontalPadding from "../../components/HorizontalPadding";
import { formatName, validateAccount } from "../../utils";
import {
  AccountInterface,
  BusinessType,
  BusinessUnitInterface,
  DeliveryType,
  PaymentMode,
  PieceInterface,
  ShipmentInterface,
  ShipmentService,
  ShipmentStatus,
} from "../../interfaces";
import {
  CheckBadgeIcon,
  ClipboardDocumentCheckIcon,
} from "@heroicons/react/24/outline";
import classNames from "classnames";
import { ClientDetails } from "../../components/Shipment/ShipmentHeader";
import { useAppSelector } from "../../store/hooks";
import { AccountSearch } from "../../components/Account/AccountSearch";
import UpdateClientModal from "../../components/Account/UpdateClientModal";
import CreateClientModal from "../../components/Account/CreateClientModal";
import { BusinessUnitOption } from "../../components/LocationSearch";
import ShipmentPiecesDetails from "../Shipments/ShipmentPiecesDetails";
import { ServiceDTO } from "../../interfaces/Dtos/ServiceDTO";
import {
  alertService,
  getBusinessUnits,
  getDeclaredShipmentDocument,
  getService,
  getWeights,
  loaderService,
  PrintShipmentLabel,
  saveShipment,
} from "../../services";
import {
  FormSearch,
  FormTextAreaInput,
  FormTextInput,
} from "../../components/FormFields";
import {
  LinkText,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons";
import { ShipmentItem } from "../Shipments/ShipmentResume";
import { useNavigate } from "react-router-dom";
import { OriginModal } from "../../components/Shipment/OriginModal";
import { saveLiabilityWaver } from "../../services/liabilityWaiverServices";

enum Phase {
  FORM,
  RESUME,
}

const InternalMail: FC = () => {
  const navigate = useNavigate();
  const loading = loaderService.useIsLoading();
  const user = useAppSelector((state) => state.user);
  const buSource = useAppSelector((state) => state.user.businessUnit);
  const countries = useAppSelector((state) => state.inmutable.countries);
  const pieceCategories = useAppSelector(
    (state) => state.inmutable.pieceCategories
  );
  const applicationID = useAppSelector(
    (state) => state.inmutable.appData.applicationID
  );

  const [phase, setPhase] = useState(Phase.FORM);
  const [cleaned, setCleaned] = useState(false);
  const [shipper, setShipper] = useState<AccountInterface>();
  const [consignee, setConsignee] = useState<AccountInterface>();
  const [buConsignee, setBUConsignee] = useState<BusinessUnitInterface>();
  const [reference, setReference] = useState("");
  const [service, setService] = useState<ServiceDTO>();
  const [pieces, setPieces] = useState<PieceInterface[]>([]);
  const [physicalWeight, setPhysicalWeight] = useState(0);
  const [dimensionalWeight, setDimensionalWeight] = useState(0);
  const [chargedWeight, setChargedlWeight] = useState(0);
  const [observations, setObservations] = useState("");
  const [shipment, setShipment] = useState<ShipmentInterface>();
  const [businessUnitSearch, setBusinessUnitSearch] = useState("");
  const [businessUnits, setBusinessUnits] = useState<BusinessUnitInterface[]>(
    []
  );

  const [liabilityId, setLiabilityId] = useState<string>();
  const [liabilityName, setLiabilityName] = useState<string>();
  const [isPhysicalLiability, setIsPhysicalLiability] = useState(false);
  const [liabilityFingerprint, setLiabilityFingerprint] = useState<string>();
  const [liabilityObservation, setLiabilityObservation] = useState<string>();
  const [liabilityAttachments, setLiabilityAttachments] = useState<string[]>(
    []
  );

  const [openShipperEditModal, setOpenShipperEditModal] = useState(false);
  const [openConsigneeEditModal, setOpenConsigneeEditModal] = useState(false);
  const [openShipperCreateModal, setOpenShipperCreateModal] = useState(false);
  const [openOriginWaiverModal, setOpenOriginWaiverModal] = useState(false);
  const [openConsigneeCreateModal, setOpenConsigneeCreateModal] =
    useState(false);

  const isShipperEnterprise = useMemo(() => {
    return (
      !!shipper?.agreementID ||
      (shipper?.listAuthorizingAccount?.length ?? 0) > 0
    );
  }, [shipper]);

  const isConsigneeEnterprise = useMemo(() => {
    return (
      !!consignee?.agreementID ||
      (consignee?.listAuthorizingAccount?.length ?? 0) > 0
    );
  }, [consignee]);

  const businessUnitOptions = useMemo(() => {
    const buSourceId = buSource?.id ?? user.businessUnit?.id;

    const business = businessUnits.filter(
      (businessUnit) => businessUnit.id !== buSourceId && !businessUnit.isLocked
    );

    // Delete duplicates by id
    const uniqueIds = new Set();
    const result: BusinessUnitInterface[] = [];
    business.forEach((businessUnit, index) => {
      if (!uniqueIds.has(businessUnit.id)) {
        uniqueIds.add(businessUnit.id);
        result.push(businessUnit);
      }
    });

    result.sort((a, b) => {
      const tokens = businessUnitSearch.split(" ");

      const aMatch = tokens.some((token) => token === a.code);
      const bMatch = tokens.some((token) => token === b.code);

      if (aMatch && !bMatch) return -1;
      if (!aMatch && bMatch) return 1;
      return 0;
    });

    return result;
  }, [businessUnits, user.businessUnit, businessUnitSearch, buSource?.id]);

  const statusMessage = useMemo(() => {
    if (!liabilityFingerprint && !isPhysicalLiability) {
      return "La guía NO tiene cargada la huella dactilar NI está marcada como registro físico para la Declaración Jurada.";
    } else if (liabilityFingerprint) {
      return "La guía tiene cargada la huella dactilar para la Declaración Jurada.";
    } else if (isPhysicalLiability) {
      return "La guía está marcada como registro físico para la Declaración Jurada.";
    }

    return "";
  }, [liabilityFingerprint, isPhysicalLiability]);

  const dataMessage = useMemo(() => {
    const warningMessage =
      "(No se podrá continuar sin estos datos para la Declaración Jurada).";

    if (!liabilityName && !liabilityId) {
      return `El nombre y cédula de identidad del remitente es requerido ${warningMessage}.`;
    } else if (!liabilityName) {
      return `El nombre del remitente es requerido ${warningMessage}.`;
    } else if (!liabilityId) {
      return `La cédula de identidad del remitente es requerido ${warningMessage}.`;
    }

    return "";
  }, [liabilityName, liabilityId]);

  const alertMessage = useMemo(() => {
    if (isPhysicalLiability) {
      return "Recuerde que debe registrar la huella y firma en el documento físico.";
    }

    return "";
  }, [isPhysicalLiability]);

  const handleShipperChange = async (shipper: AccountInterface) => {
    const validations = await validateAccount(shipper, countries);

    setShipper(shipper);
    setBUConsignee(undefined);

    if (shipper && validations.error) {
      setOpenShipperEditModal(true);
    }
  };

  const handleConsigneeChange = async (consignee: AccountInterface) => {
    const validations = await validateAccount(consignee, countries);

    setConsignee(consignee);
    setBUConsignee(undefined);

    if (shipper && validations.error) {
      setOpenConsigneeEditModal(true);
    }
  };

  const onClean = () => {
    setShipper(undefined);
    setConsignee(undefined);
    setBUConsignee(undefined);
    setReference("");
    setPieces([]);
    setObservations("");
    setCleaned(true);
    setBusinessUnitSearch("");
  };

  const onSubmit = async () => {
    const minWeight = service?.minWeight ?? 0;

    if (!user) {
      alertService.error("El usuario parece que no esta loggeado.");
      return;
    }
    if (!buSource) {
      alertService.error("No se ha cargado la tienda origen del usuario.");
      return;
    }
    if (!shipper) {
      alertService.error("El remitente es obligatorio.");
      return;
    }
    if (!consignee) {
      alertService.error("El destinatario es obligatorio.");
      return;
    }
    if (!buConsignee) {
      alertService.error("La tienda destino es obligatoria.");
      return;
    }
    if (pieces.length === 0) {
      alertService.error("Debe haber al menos una pieza.");
      return;
    }
    if (chargedWeight < minWeight) {
      alertService.error(
        `El peso a facturar debe ser mayor a ${minWeight.toFixed(2)} Kg.`
      );
      return;
    }

    const shipment: ShipmentInterface = {
      shipper,
      consignee,
      accountBillTo: shipper,
      buSource,
      deliveryType: DeliveryType.AT_HOME,
      consigneeAddress: {
        ...buConsignee.location,
        reference,
      },
      buConsignee,
      pieces,
      items: [],
      paymentMode: PaymentMode.EXEMPT,
      observations,
      service: ShipmentService.INTERNAL_MAIL,
      status: ShipmentStatus.ACTIVE,
      total: 0,
      totalChargedWeight: chargedWeight,
      totalPhysicalWeight: physicalWeight,
      totalDimensionalWeight: dimensionalWeight,
    };

    const response = await saveShipment(
      shipment,
      user.user ?? undefined,
      applicationID
    );

    if (response.didError || !response.model) {
      alertService.error(
        "Hubo un error creando la guía",
        response.errorMessage
      );
      return;
    }

    setPhase(Phase.RESUME);
    setOpenOriginWaiverModal(true);
    setShipment({
      ...shipment,
      shipmentNumber: response.model?.shipmentNumber,
      id: response.model?.shipmentHeaderID,
    });
  };

  const handleDownloadDocument = async () => {
    if (!shipment) {
      alertService.error(
        "Error al imprimir el documento",
        "No se encontraron los identificadores."
      );
      return;
    }

    alertService.info("Imprimiendo documento...");

    const fileResult = await getDeclaredShipmentDocument({
      shipmentNumber: shipment.shipmentNumber ?? "",
      shipmentHeaderID: shipment.id,
    });

    if (!!fileResult) {
      // Create a URL for the Blob
      const fileURL = URL.createObjectURL(fileResult.file);

      // Create a link element
      const a = document.createElement("a");
      a.href = fileURL;
      a.download = fileResult.fileName; // Set the filename for the downloaded file
      a.click();

      // Remember to handle Blob URL cleanup when done
      URL.revokeObjectURL(fileURL);
      alertService.success("Documento impreso satisfactoriamente.");
    } else {
      alertService.error("No se pudo descargar el documento de la guía.");
    }
  };

  const handlePrintLabel = async () => {
    if (!shipment || !shipment.shipmentNumber || !shipment.service) {
      alertService.error(
        "Error al imprimir la etiqueta",
        "No se encontraron los identificadores."
      );
      return;
    }

    alertService.info(
      "Guía: " + shipment.shipmentNumber,
      "Imprimiendo etiqueta..."
    );
    const printSucceed = await PrintShipmentLabel(
      +shipment.shipmentNumber,
      shipment.service ?? 0,
      shipment.buSource.code,
      user.user?.userLogin ?? ""
    );

    if (!!printSucceed) {
      alertService.success(
        "Guía:" + shipment.shipmentNumber,
        "La etiqueta se imprimió correctamente."
      );
    }
  };

  const handleFinish = async () => {
    if (!shipment?.id) {
      alertService.error("No hay ninguna guía cargada");
      return;
    }
    if (!liabilityName || !liabilityId) {
      alertService.error("Parece que no se ha cargado la declaración jurada.");
      return;
    }

    const liabilityWaiver = await saveLiabilityWaver(
      liabilityFingerprint,
      liabilityAttachments,
      [shipment.id],
      isPhysicalLiability,
      liabilityName,
      liabilityId,
      user.user?.userLogin ?? "",
      1,
      liabilityObservation
    );

    if (liabilityWaiver.didError) {
      loaderService.stop();
      alertService.error(liabilityWaiver.errorMessage);
      return;
    }

    navigate(`/shipments/${shipment?.shipmentNumber}`);
  };

  const filterBusinessUnit = (
    option: BusinessUnitInterface,
    search: string
  ) => {
    const tokens = search
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .split(" ");

    return tokens.every((token) => {
      return (
        token === "-" ||
        option.location.name.toLowerCase().includes(token) ||
        option.code?.toLowerCase().includes(token) ||
        option.location.address.toLowerCase().includes(token) ||
        !!option.location.postalCode?.toLowerCase().includes(token) ||
        !!option.location.reference?.toLowerCase().includes(token)
      );
    });
  };

  useEffect(() => {
    const serviceID = ShipmentService.INTERNAL_MAIL;
    if (serviceID === undefined) {
      return;
    }

    const afunction = async () => {
      const service = await getService(serviceID);

      if (!service.didError && service.model) {
        setService(service.model);
      }
    };

    afunction();
  }, []);

  useEffect(() => {
    if (pieces.length === 0) {
      setPhysicalWeight(0);
      setDimensionalWeight(0);
      setChargedlWeight(0);
      return;
    }

    const getShipmentWeight = async () => {
      const weights = await getWeights(
        236,
        10,
        shipper?.id,
        undefined,
        ShipmentService.INTERNAL_MAIL,
        PaymentMode.EXEMPT,
        pieces
      );

      if (!weights || weights.didError || !weights.model) return;

      setPhysicalWeight(weights.model.physicalWeight);
      setDimensionalWeight(weights.model.dimensionalWeight);
      setChargedlWeight(weights.model.chargedWeight);
    };

    getShipmentWeight();
  }, [pieces, shipper?.id]);

  useEffect(() => {
    const afunction = async () => {
      const businessUnits = await getBusinessUnits(true);
      if (businessUnits !== null) {
        setBusinessUnits(
          businessUnits.filter(
            (b) =>
              (b.type === BusinessType.VIRTUAL_STORE_FOR_INTERNAL_USE ||
                b.type === BusinessType.INTERNAL ||
                b.type === BusinessType.EXTERNAL) &&
              !b.isLocked
          )
        );
      }
    };

    afunction();
  }, []);

  return (
    <main className="lg:pl-72 pb-32">
      {/* Header */}
      <div className="py-8 sm:px-6 lg:px-8 bg-white relative flex items-center justify-between h-32">
        <header className="ml-4 text-2xl font-bold text-gray-700 ">
          Correo Interno
        </header>
      </div>

      <div>
        <HorizontalPadding paddingTop>
          <div
            className={classNames(
              "flex flex-col",
              phase !== Phase.FORM && "hidden"
            )}
          >
            <div className="flex justify-end">
              <LinkText text="Limpiar todo" onClick={onClean} />
            </div>

            {/* Shipper and Consignee description */}
            <div className="flex flex-col flex-1 px-6 py-4 bg-white rounded-md border">
              <div className="relative flex items-center">
                <div className="gap-8 grid w-full grid-cols-2">
                  {/* Shipper */}
                  <div className="flex flex-1 flex-col">
                    <div className="flex flex-col xl:flex-row xl:gap-4 xl:items-center">
                      <p className="font-semibold">Remitente:</p>
                      <div className="flex items-center truncate">
                        {formatName(shipper?.accountFullName ?? "")}

                        <CheckBadgeIcon
                          title="Cliente Crédito Corporativo"
                          data-te-toggle="tooltip"
                          className={classNames(
                            "ml-2 inline h-5 w-5 text-indigo-600",
                            !isShipperEnterprise && "hidden"
                          )}
                        />
                      </div>
                    </div>

                    <ClientDetails client={shipper} />
                  </div>

                  {/* Consignee */}
                  <div className="flex flex-1 flex-col">
                    <div className="flex flex-col xl:flex-row xl:gap-4 xl:items-center">
                      <p className="font-semibold">Destinatario:</p>
                      <div className="flex items-center truncate">
                        {formatName(consignee?.accountFullName ?? "")}

                        <CheckBadgeIcon
                          title="Cliente Crédito Corporativo"
                          data-te-toggle="tooltip"
                          className={classNames(
                            "ml-2 inline h-5 w-5 text-indigo-600",
                            !isConsigneeEnterprise && "hidden"
                          )}
                        />
                      </div>
                    </div>

                    <ClientDetails client={consignee} />
                  </div>
                </div>
              </div>

              <div>
                <div className="flex flex-1 flex-col pt-4">
                  {/* buSource */}
                  <div className="flex flex-1 flex-col">
                    <div className="flex items-center gap-4">
                      <p className="font-semibold mr-2">Origen:</p>
                      <div className="flex flex-col">
                        {!!buSource?.code ? (
                          <p className="text-sm">
                            {buSource?.code} - {buSource?.location.name}
                          </p>
                        ) : (
                          <p className="text-sm">{buSource?.location.name}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex flex-1 flex-col mt-2">
                    <div className="flex items-start gap-4">
                      <p className="font-semibold">Destino:</p>
                      <div className="flex flex-col">
                        <p
                          className={classNames(
                            "text-sm",
                            !buConsignee && "hidden"
                          )}
                        >
                          {buConsignee?.code} - {buConsignee?.location?.name}
                        </p>

                        <p className="text-xs">
                          {buConsignee?.location.name}
                          {" - "}
                          {buConsignee?.location.address}{" "}
                          {!!buConsignee?.location.postalCode
                            ? `(código postal: ${buConsignee?.location.postalCode})`
                            : ""}
                        </p>
                        <p className="text-xs">
                          {buConsignee?.location.reference}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="flex flex-1 flex-col bg-white rounded-lg border px-8 py-6 gap-6 mt-8">
              <div className="flex flex-1 2xl:flex-row flex-col gap-4">
                {/* Search shipper */}
                <div className="relative flex flex-1">
                  <AccountSearch
                    title="Remitente"
                    selected={shipper}
                    placeholder="Buscar remitente..."
                    ignoredAccounts={[
                      {
                        abreviationName: "J-",
                        identificationNumber: "306842675",
                      },
                    ]}
                    onSelectClient={handleShipperChange}
                    openCreationModal={() => setOpenShipperCreateModal(true)}
                  />
                </div>

                {/* Search consignee */}
                <div className="flex flex-1 flex-col">
                  <AccountSearch
                    title="Destinatario"
                    selected={consignee}
                    placeholder="Buscar destinatario..."
                    ignoredAccounts={[
                      {
                        abreviationName: "J-",
                        identificationNumber: "306842675",
                      },
                    ]}
                    onSelectClient={handleConsigneeChange}
                    openCreationModal={() => setOpenConsigneeCreateModal(true)}
                  />
                </div>
              </div>

              <div className="flex flex-1 2xl:flex-row flex-col gap-4">
                <div className="flex flex-1 flex-col gap-2">
                  <label className="block text-m font-medium text-gray-900 truncate">
                    Destino
                  </label>

                  <div className="relative">
                    <FormSearch
                      value={businessUnitSearch}
                      options={businessUnitOptions}
                      onChange={(e) => setBusinessUnitSearch(e.target.value)}
                      placeholder="Buscar tienda..."
                      filter={filterBusinessUnit}
                      onSelectOption={(option) => {
                        setBUConsignee(option);
                        setBusinessUnitSearch(
                          `${option.code} - ${option.location.name}`
                        );
                      }}
                      onChangeFocus={(focus) => {
                        if (!focus && buConsignee)
                          setBusinessUnitSearch(
                            `${buConsignee.code} - ${buConsignee.location.name}`
                          );
                      }}
                      RenderOption={({ option }) => (
                        <BusinessUnitOption {...option} />
                      )}
                    />
                  </div>
                </div>

                <div className="flex flex-1 flex-col">
                  <FormTextInput
                    value={reference}
                    onChange={(event) => setReference(event.target.value)}
                    name="reference"
                    label="Dirección de entrega"
                  />
                </div>
              </div>

              <hr className="border-gray-300 my-6" />

              <div className="flex flex-1 flex-col gap-2">
                <ShipmentPiecesDetails
                  id=""
                  typeDisabled
                  applyMinValue
                  pieces={pieces}
                  cleaned={cleaned}
                  setCleaned={setCleaned}
                  setPieces={setPieces}
                  maxSumWeightWarn={service?.maxWeight}
                  service={ShipmentService.INTERNAL_MAIL}
                  pieceCategories={pieceCategories}
                  paymentMode={PaymentMode.EXEMPT}
                  setServiceType={() => {}}
                  serviceOptions={[]}
                  chargedWeight={chargedWeight}
                  physicalWeight={physicalWeight}
                  dimensionalWeight={dimensionalWeight}
                />
              </div>

              <hr className="border-gray-300 my-6" />

              {/* Observations */}
              <div className="flex flex-1 flex-col">
                <FormTextAreaInput
                  rows={4}
                  maxLength={192}
                  value={observations}
                  onChange={(event) => setObservations(event.target.value)}
                  name="observations"
                  label="Observaciones"
                  className="resize-none"
                />
              </div>
            </div>

            <div className="flex flex-1 justify-end mt-8">
              <PrimaryButton
                disabled={loading}
                onClick={async () => {
                  loaderService.start();
                  await onSubmit();
                  loaderService.stop();
                }}
                className="!px-8"
              >
                Crear Guía
              </PrimaryButton>
            </div>
          </div>

          <div
            className={classNames(
              "flex flex-col",
              phase !== Phase.RESUME && "hidden"
            )}
          >
            <div className="flex flex-1 flex-col bg-white rounded-lg border px-8 pb-6 pt-4 gap-8">
              <table className="table-auto w-full">
                <thead>
                  <tr>
                    <th className="text-left px-4 py-2 font-semibold text-xs">
                      GUÍA
                    </th>

                    <th className="text-left px-4 py-2 font-semibold text-xs">
                      DESTINATARIO
                    </th>

                    <th className="text-left px-4 py-2 font-semibold text-xs">
                      DESTINO
                    </th>

                    <th className="text-right px-4 py-2 font-semibold text-xs">
                      MONTO
                    </th>

                    <th className="text-left px-4 py-2 font-bold text-gray-700 text-xs w-5" />
                  </tr>
                </thead>
                <tbody>
                  {shipment && <ShipmentItem index={0} shipment={shipment} />}
                </tbody>
              </table>

              <div>
                <p
                  className={classNames(
                    "text-red-500",
                    !dataMessage && "hidden"
                  )}
                >
                  {dataMessage}
                </p>

                <p
                  className={classNames(
                    !liabilityFingerprint &&
                      !isPhysicalLiability &&
                      "text-red-500"
                  )}
                >
                  {statusMessage}
                </p>
                <p className={classNames(!alertMessage && "hidden")}>
                  {alertMessage}
                </p>
              </div>

              <div className="flex flex-1 gap-4 justify-start items-center">
                <SecondaryButton
                  className="w-48"
                  disabled={loading}
                  onClick={async () => {
                    loaderService.start();
                    await handlePrintLabel();
                    loaderService.stop();
                  }}
                >
                  Imprimir Etiquetas
                </SecondaryButton>

                <SecondaryButton
                  className="w-48"
                  disabled={loading}
                  onClick={async () => {
                    loaderService.start();
                    await handleDownloadDocument();
                    loaderService.stop();
                  }}
                >
                  Imprimir Documentos
                </SecondaryButton>

                <SecondaryButton
                  className="w-52"
                  onClick={() => setOpenOriginWaiverModal(true)}
                >
                  Cargar Declaración Jurada
                </SecondaryButton>
              </div>
            </div>

            <div className="flex flex-1 gap-4 justify-end items-center mt-8">
              <PrimaryButton
                className="w-32"
                disabled={
                  (!isPhysicalLiability && !liabilityFingerprint) ||
                  !liabilityName ||
                  !liabilityId
                }
                onClick={handleFinish}
              >
                <ClipboardDocumentCheckIcon
                  aria-hidden="true"
                  className="h-5 w-5 flex-none text-white-400 mr-2"
                />
                LISTO
              </PrimaryButton>
            </div>
          </div>
        </HorizontalPadding>
      </div>

      <CreateClientModal
        openModal={openShipperCreateModal}
        setOpenModal={setOpenShipperCreateModal}
        setSelectedItem={handleShipperChange}
      />

      <CreateClientModal
        openModal={openConsigneeCreateModal}
        setOpenModal={setOpenConsigneeCreateModal}
        setSelectedItem={handleConsigneeChange}
      />

      {shipper && (
        <UpdateClientModal
          selectedItem={shipper}
          openModal={openShipperEditModal}
          setSelectedItem={handleShipperChange}
          setOpenModal={setOpenShipperEditModal}
        />
      )}

      {consignee && (
        <UpdateClientModal
          selectedItem={consignee}
          openModal={openConsigneeEditModal}
          setSelectedItem={handleConsigneeChange}
          setOpenModal={setOpenConsigneeEditModal}
        />
      )}

      {shipment && shipment.shipmentNumber && (
        <OriginModal
          openModal={openOriginWaiverModal}
          setOpenModal={setOpenOriginWaiverModal}
          shipmentNumbers={[shipment.shipmentNumber]}
          shipments={[shipment]}
          shipper={shipment.shipper}
          setExtFingerprint={setLiabilityFingerprint}
          setExtAttachments={setLiabilityAttachments}
          setExtName={setLiabilityName}
          setExtId={setLiabilityId}
          setExtObservation={setLiabilityObservation}
          setIsPhysical={setIsPhysicalLiability}
        />
      )}
    </main>
  );
};

export default InternalMail;
